import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import SearchIcon from '@mui/icons-material/Search';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Eyebrow from '../components/common/Eyebrow';
import ReportDetailModal from './portals/medical/ReportDetailModal';
import {
  DashboardKpisShimmer,
  DashboardBookingsShimmer,
  DashboardReportsShimmer,
} from '../components/shimmer';
import { useAuth } from '../context/AuthContext';
import { useListBookingsQuery } from '../store/bookingsApi';
import { ROUTES, buildRoute } from '../constants/routes';

// Maps backend status → display label + colour.
const BOOKING_STATUS = {
  confirmed: { label: 'Confirmed',   tone: '#3D7A52', bg: '#D1FAE5', active: true },
  assigned:  { label: 'Assigned',    tone: '#1D4ED8', bg: '#DBEAFE', active: true },
  enroute:   { label: 'En Route',    tone: '#C97D08', bg: '#FEF3C7', active: true },
  collected: { label: 'Collected',   tone: '#7C3AED', bg: '#EDE9FE', active: false },
  reported:  { label: 'Report Ready',tone: '#065F46', bg: '#D1FAE5', active: false },
  cancelled: { label: 'Cancelled',   tone: '#991B1B', bg: '#FEE2E2', active: false },
};

// Backend status → the style/label used in the Reports table row.
const REPORT_BADGE = {
  normal:   { bg: '#D1FAE5', color: '#065F46', label: 'Normal' },
  abnormal: { bg: '#FEE2E2', color: '#991B1B', label: 'Abnormal' },
  low:      { bg: '#FEF3C7', color: '#92400E', label: 'Low' },
  high:     { bg: '#FEE2E2', color: '#991B1B', label: 'High' },
  critical: { bg: '#FEE2E2', color: '#991B1B', label: 'Critical' },
  pending:  { bg: '#EDE9E1', color: '#6B6B65', label: 'Pending' },
};

// Filter → normalised server status value. Keep the option labels readable.
const STATUS_FILTER_OPTIONS = [
  { value: '',          label: 'All Statuses' },
  { value: 'normal',    label: 'Normal' },
  { value: 'abnormal',  label: 'Abnormal' },
  { value: 'low',       label: 'Low' },
  { value: 'high',      label: 'High' },
  { value: 'pending',   label: 'Pending' },
];

// Cancellation eligibility — same rule as the backend (3h before slot).
const canCancelBooking = (b) => {
  if (!b || ['cancelled', 'collected', 'reported'].includes(b.status)) return false;
  if (!b.schedule?.date) return true;
  const m = String(b.schedule.slot || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  let h = m ? Number(m[1]) : 0;
  const mn = m ? Number(m[2]) : 0;
  const ap = (m?.[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  const [y, mo, d] = String(b.schedule.date).split('-').map(Number);
  if (!y) return true;
  return (new Date(y, mo - 1, d, h, mn).getTime() - Date.now()) / 3_600_000 >= 3;
};

// Friendly "5 Apr 2026" from either an ISO date or a 'YYYY-MM-DD' string.
const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  const { data: bookingsData, isLoading: loadingBookings } = useListBookingsQuery();
  const bookings = useMemo(() => bookingsData?.bookings || [], [bookingsData]);

  // ─── Split bookings: reports-ready vs still in flight ──────────────
  // A booking moves to the "My Reports" table the moment IT Dose
  // registers the visit (i.e. `itdose.visit_id` is stamped) — that's
  // when the visit-level PDF becomes downloadable. The remainder show
  // up in "My Bookings" until that happens.
  const reportReadyBookings = useMemo(
    () => bookings.filter((b) => b?.itdose?.visit_id),
    [bookings],
  );
  const activeBookings = useMemo(
    () => bookings.filter((b) => !b?.itdose?.visit_id && b?.status !== 'cancelled'),
    [bookings],
  );
  const recentBookings = activeBookings.slice(0, 5);

  // ─── Synthesise "report rows" from report-ready bookings ───────────
  // The table renders one row per ordered test (booking.items[i]) so
  // patients can scan-read their results test-by-test. Every row keeps
  // a back-reference to the parent booking so the modal can build the
  // visit object with the FULL test list (matches portal behaviour —
  // clicking any test opens the visit-level PDF that bundles all).
  const reports = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = reportReadyBookings.flatMap((b) =>
      (b.items || []).map((it, i) => ({
        report_id: `${b.booking_id}-${i}`,
        booking_id: b.booking_id,
        test_name: it.name,
        test_code: it.item_id,
        icon: it.icon || '🧪',
        reported_at: b.itdose?.registered_at || b.updatedAt,
        sample_date: b.schedule?.date,
        centre: 'TrustLab',
        // All these are "reports ready" by definition of the filter
        // above; status="normal" maps to the green REPORT_BADGE.
        status: 'normal',
        _booking: b,
      })),
    );
    return rows.filter((r) => {
      if (q && !`${r.test_name} ${r.test_code}`.toLowerCase().includes(q)) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      return true;
    });
  }, [reportReadyBookings, search, statusFilter]);
  const loadingReports = loadingBookings;

  // Build the synthetic `visit` object ReportDetailModal expects. The
  // tests array carries every item in the booking — same UX the portal
  // ships, so clicking any single test row shows the full visit + the
  // one combined PDF.
  const visitForReport = useMemo(() => {
    const b = selectedReport?._booking;
    if (!b) return null;
    return {
      visitNo: b.itdose?.visit_id || b.booking_id,
      uhid: user?.uhid || '—',
      patient: b.patient?.name || user?.name || '',
      billDate: b.itdose?.registered_at
        ? new Date(b.itdose.registered_at).toLocaleDateString()
        : (b.schedule?.date || ''),
      tests: (b.items || []).map((it) => ({
        id: it.item_id,
        name: it.name,
        dept: 'Laboratory',
      })),
      status: 'reported',
    };
  }, [selectedReport, user]);

  /* ── Dynamic KPIs ── */
  const stats = useMemo(() => {
    const thisYear = new Date().getFullYear();

    const testsThisYear = bookings.reduce((sum, b) => {
      const yr = new Date(b.createdAt || b.created_at || 0).getFullYear();
      if (yr !== thisYear) return sum;
      return sum + (b.items || []).reduce((n, i) => n + (i.quantity || 1), 0);
    }, 0);

    const totalSaved = bookings.reduce(
      (sum, b) => sum + (Number(b.amounts?.discount) || 0),
      0
    );

    return [
      { ico: '🧪', label: 'Tests This Year',  value: testsThisYear },
      { ico: '📋', label: 'Reports Ready',    value: reports.length },
      { ico: '🚚', label: 'Active Bookings',  value: activeBookings.length },
      { ico: '💸', label: 'You\'ve Saved',    value: `₹${totalSaved.toLocaleString()}`, valueColor: 'var(--sage)' },
    ];
  }, [bookings, reports, activeBookings]);

  return (
    <Box sx={{ bgcolor: 'var(--cream)', minHeight: '100vh' }}>

      {/* ═══════════ HEADER ═══════════ */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          pt: { xs: 10, md: 11 },
          pb: { xs: 4, md: 4.5 },
          px: { xs: 2, md: 7 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background radial accent */}
        <Box
          sx={{
            position: 'absolute',
            top: -150,
            right: -150,
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(61,122,82,.15) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="xl" sx={{ maxWidth: 1300, position: 'relative', zIndex: 2 }}>
          <Grid container alignItems="flex-end" spacing={2}>
            {/* Left 8 cols: greeting */}
            <Grid item xs={12} md={8}>
              <Eyebrow white>My Reports</Eyebrow>
              <Typography
                className="serif"
                sx={{ fontSize: { xs: 32, md: 38 }, fontWeight: 600, color: '#fff', mt: 1, lineHeight: 1.1 }}
              >
                Hello, {user?.name || 'Priya'} 👋
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', mt: 0.75 }}>
                Hyderabad · Active Member
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════ KPI CARDS ═══════════ */}
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 7 }, pt: 3, pb: 0 }}>
        {(loadingBookings || loadingReports) ? (
          <DashboardKpisShimmer />
        ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {stats.map((card) => (
            <Grid item xs={3} key={card.label}>
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '20px',
                  p: 3,
                  border: '1px solid var(--border-l, #EDE9E1)',
                  boxShadow: '0 4px 20px rgba(21,42,30,.06)',
                  height: '100%',
                }}
              >
                <Typography sx={{ fontSize: 28, lineHeight: 1, mb: 1.5 }}>{card.ico}</Typography>
                <Typography
                  className="serif"
                  sx={{
                    fontSize: { xs: 30, md: 36 },
                    fontWeight: 600,
                    color: card.valueColor || 'var(--ink)',
                    lineHeight: 1,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--stone)', mt: 1, fontWeight: 300 }}>
                  {card.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        )}
      </Container>

      {/* ═══════════ MY BOOKINGS ═══════════ */}
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 7 }, pb: 4 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l, #EDE9E1)',
            borderRadius: '18px',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3, py: 2.25,
              borderBottom: '1px solid var(--border-l)',
              flexWrap: 'wrap', gap: 1.25,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <EventAvailableOutlinedIcon sx={{ color: 'var(--sage)', fontSize: 20 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                My Bookings
              </Typography>
              {activeBookings.length > 0 && (
                <Box component="span" sx={{
                  bgcolor: 'var(--mint)', color: 'var(--forest)',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                  borderRadius: 100, px: 1.125, py: 0.25, ml: 0.5,
                }}>
                  {activeBookings.length}
                </Box>
              )}
            </Stack>
            <Button
              size="small"
              onClick={() => navigate(ROUTES.TESTS)}
              sx={{
                color: 'var(--sage)', fontSize: 12, fontWeight: 700,
                textTransform: 'none',
                '&:hover': { bgcolor: 'var(--mint)' },
              }}
            >
              + New Booking
            </Button>
          </Box>

          {/* Body */}
          {loadingBookings ? (
            <DashboardBookingsShimmer count={3} />
          ) : recentBookings.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center', color: 'var(--stone)' }}>
              <Typography sx={{ fontSize: 28, mb: 1 }}>📅</Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                No bookings yet
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5, mb: 2 }}>
                Book a test and a Trustian will visit you at home.
              </Typography>
              <Button
                variant="contained" size="small"
                onClick={() => navigate(ROUTES.TESTS)}
                sx={{
                  bgcolor: 'var(--sage)', color: '#fff', borderRadius: 100,
                  textTransform: 'none', fontWeight: 700, px: 2.5,
                  '&:hover': { bgcolor: 'var(--forest)' },
                }}
              >
                Browse Tests
              </Button>
            </Box>
          ) : (
            recentBookings.map((b, idx) => {
              const s = BOOKING_STATUS[b.status] || BOOKING_STATUS.confirmed;
              const itemsSummary = (b.items || [])
                .map((i) => i.name).slice(0, 2).join(', ') +
                (b.items.length > 2 ? ` + ${b.items.length - 2} more` : '');
              return (
                <Box
                  key={b.booking_id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '44px 1.6fr 1.3fr 1fr 130px' },
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    px: 3, py: 2.25,
                    borderBottom: idx < recentBookings.length - 1 ? '1px solid var(--border-l)' : 'none',
                    '&:hover': { bgcolor: 'var(--cream)' },
                    transition: 'background 0.15s',
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '10px',
                      bgcolor: 'var(--mint)', border: '1px solid var(--sage-p)',
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: 'var(--sage)' }} />
                  </Box>

                  {/* Booking id + items */}
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography
                        sx={{
                          fontSize: 12, fontWeight: 700, color: 'var(--sage)',
                          fontFamily: "'JetBrains Mono', monospace",
                          letterSpacing: '0.04em',
                        }}
                      >
                        {b.booking_id}
                      </Typography>
                      {b.itdose?.visit_id ? (
                        <Typography
                          sx={{
                            fontSize: 11, fontWeight: 700, color: 'var(--sage)',
                            fontFamily: "'JetBrains Mono', monospace",
                            bgcolor: 'var(--mint)',
                            border: '1px solid var(--sage-p)',
                            borderRadius: 100,
                            px: 0.875, py: 0.125,
                          }}
                        >
                          Visit {b.itdose.visit_id}
                        </Typography>
                      ) : (
                        ['collected', 'reported'].includes(b.status) && (
                          <Typography
                            sx={{
                              fontSize: 11, fontWeight: 700, color: 'var(--stone)',
                              bgcolor: 'var(--cream)',
                              border: '1px solid var(--border-l)',
                              borderRadius: 100,
                              px: 0.875, py: 0.125,
                            }}
                          >
                            Visit pending
                          </Typography>
                        )
                      )}
                    </Stack>
                    <Typography
                      sx={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', mt: 0.25 }}
                      noWrap
                    >
                      {itemsSummary || '—'}
                    </Typography>
                  </Box>

                  {/* Schedule */}
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--stone)', textTransform: 'uppercase', mb: 0.25 }}>
                      Scheduled
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--ink)' }}>
                      {b.schedule?.date} · {b.schedule?.slot}
                    </Typography>
                  </Box>

                  {/* Total */}
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--stone)', textTransform: 'uppercase', mb: 0.25 }}>
                      Total
                    </Typography>
                    <Typography className="serif" sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>
                      ₹{(b.amounts?.total ?? 0).toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Status + Track */}
                  <Stack spacing={1} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        bgcolor: s.bg, color: s.tone,
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                        px: 1.375, py: 0.5, borderRadius: 100,
                      }}
                    >
                      {s.label}
                    </Box>
                    <Button
                      size="small"
                      onClick={() => navigate(`${ROUTES.TRACK}?id=${encodeURIComponent(b.booking_id)}`)}
                      sx={{
                        bgcolor: s.active ? 'var(--forest)' : 'transparent',
                        color: s.active ? '#fff' : 'var(--sage)',
                        border: s.active ? 'none' : '1px solid var(--sage-p)',
                        borderRadius: 100, textTransform: 'none',
                        fontWeight: 700, fontSize: 12,
                        px: 1.75, py: 0.5,
                        '&:hover': {
                          bgcolor: s.active ? 'var(--ink)' : 'var(--mint)',
                        },
                      }}
                    >
                      {s.active ? 'Track →' : 'View'}
                    </Button>
                    {canCancelBooking(b) && (
                      <Button
                        size="small"
                        onClick={() => navigate(buildRoute(ROUTES.CANCEL_BOOKING, { id: b.booking_id }))}
                        sx={{
                          color: 'var(--red, #DC2626)', textTransform: 'none',
                          fontWeight: 600, fontSize: 11.5, px: 0.5, py: 0,
                          minWidth: 0,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </Container>

      {/* ═══════════ REPORTS TABLE ═══════════ */}
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 7 }, pb: 9 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l, #EDE9E1)',
            borderRadius: '18px',
            overflow: 'hidden',
          }}
        >
          {/* ── Search & filter bar ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2.25,
              borderBottom: '1px solid var(--border-l, #EDE9E1)',
              flexWrap: 'wrap',
              gap: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>My Reports</Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <TextField
                placeholder="Search reports..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'var(--stone)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: '100%', sm: 200 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '9px',
                    fontSize: 13,
                    '& fieldset': { borderColor: 'var(--border-l, #EDE9E1)' },
                    '&:hover fieldset': { borderColor: 'var(--sage)' },
                    '&.Mui-focused fieldset': { borderColor: 'var(--sage)' },
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    borderRadius: '9px',
                    fontSize: 13,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-l, #EDE9E1)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--sage)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--sage)' },
                  }}
                >
                  {STATUS_FILTER_OPTIONS.map((o) => (
                    <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* ── Table header ── */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '48px 1fr 140px 120px 120px 110px',
              px: 3,
              py: 1.25,
              bgcolor: 'var(--cream)',
              borderBottom: '1px solid var(--border-l, #EDE9E1)',
            }}
          >
            {['', 'Test Name', 'Date', 'Centre', 'Status', ''].map((h) => (
              <Typography
                key={h || 'empty'}
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--stone)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* ── Report rows ── */}
          {loadingReports ? (
            <DashboardReportsShimmer count={5} />
          ) : reports.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 7, px: 3 }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: 'var(--mint)', border: '1.5px solid var(--sage-p)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2,
              }}>
                <ScienceOutlinedIcon sx={{ fontSize: 36, color: 'var(--sage)' }} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 0.75 }}>
                {search || statusFilter ? 'No reports match your filters' : 'No reports yet'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'var(--stone)', fontWeight: 300, maxWidth: 380, mx: 'auto', mb: 2.5 }}>
                {search || statusFilter
                  ? 'Try clearing the search or status filter to see all your reports.'
                  : 'Once your booked tests are processed, your reports will appear here — usually within 6 hours of sample collection.'}
              </Typography>
              {!(search || statusFilter) && (
                <Button
                  variant="contained"
                  onClick={() => navigate(ROUTES.TESTS)}
                  sx={{
                    bgcolor: 'var(--sage)', color: '#fff', borderRadius: 100,
                    textTransform: 'none', fontWeight: 700, px: 3,
                    '&:hover': { bgcolor: 'var(--forest)' },
                  }}
                >
                  Book a Test
                </Button>
              )}
            </Box>
          ) : (
            reports.map((r, idx) => {
              const badge = REPORT_BADGE[r.status] || REPORT_BADGE.pending;
              return (
                <Box
                  key={r.report_id || r._id}
                  onClick={() => setSelectedReport(r)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '48px 1fr 140px 140px 120px 110px',
                    px: 3,
                    py: 1.75,
                    borderBottom: idx < reports.length - 1 ? '1px solid var(--border-l, #EDE9E1)' : 'none',
                    alignItems: 'center',
                    transition: 'background 0.15s',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'var(--mint)' },
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      bgcolor: 'var(--mint)', border: '1px solid var(--sage-p, #C8EDD8)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16,
                    }}
                  >
                    {r.icon || '🧪'}
                  </Box>

                  {/* Test name + code */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }} noWrap>
                      {r.test_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11, color: 'var(--stone)', mt: 0.25,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}
                    >
                      {r.report_id || r.test_code || '—'}
                    </Typography>
                  </Box>

                  {/* Date */}
                  <Typography sx={{ fontSize: 13, color: 'var(--char, #2C2C28)' }}>
                    {formatDate(r.reported_at || r.sample_date)}
                  </Typography>

                  {/* Centre */}
                  <Typography sx={{ fontSize: 13, color: 'var(--stone)' }} noWrap>
                    {r.centre || '—'}
                  </Typography>

                  {/* Status badge */}
                  <Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        bgcolor: badge.bg, color: badge.color,
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                        px: 1.375, py: 0.5, borderRadius: 100,
                      }}
                    >
                      {badge.label}
                    </Box>
                  </Box>

                  {/* View button */}
                  <Box>
                    <Button
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setSelectedReport(r); }}
                      sx={{
                        bgcolor: 'var(--sage)', color: '#fff',
                        fontWeight: 700, fontSize: 12,
                        borderRadius: 100, textTransform: 'none',
                        px: 2, py: 0.625,
                        '&:hover': { bgcolor: 'var(--forest)' },
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Container>

      {/* Report Detail Modal — same component the medical & lab portals
          use. Opens IT Dose's visit-level PDF in a new tab via
          `buildItdoseReportPdfUrl`. We don't pass `onRaiseQuery`, so the
          Raise-Query CTA is hidden for patient users. */}
      <ReportDetailModal
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        reportId={selectedReport?.report_id}
        visit={visitForReport}
      />
    </Box>
  );
};

export default Dashboard;
